// Detects when a newer production build has been deployed and shows a manual
// "Actualizar" banner (no auto-reload — vendors are often mid-form).
//
// How it works: on mount it fetches /version.json and records that as the
// baseline (= the version this tab is running). It re-checks on an interval and
// whenever the tab regains focus. When the fetched version differs from the
// baseline, a new build is live, so we surface the banner. The id is stamped
// per build by scripts/stamp-version.js.
//
// Self-contained (inline styles, no Polaris) so it can mount at the very top of
// the tree, outside the Frame/route shell, and show on every page.
import React, { useEffect, useRef, useState } from "react";

// How often to re-check while the tab is open.
const POLL_MS = 60 * 1000;

async function fetchVersion() {
  try {
    // Cache-bust + no-store so we always hit the freshly deployed file, never
    // a CDN/browser-cached copy.
    const base = process.env.PUBLIC_URL || "";
    const res = await fetch(`${base}/version.json?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.version ? String(data.version) : null;
  } catch (_) {
    return null; // offline / transient — try again next tick
  }
}

function VersionChecker() {
  const baselineRef = useRef(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const v = await fetchVersion();
      if (cancelled || !v || v === "dev") return;
      if (baselineRef.current == null) {
        baselineRef.current = v; // first read = the version we're running
      } else if (v !== baselineRef.current) {
        setUpdateAvailable(true);
      }
    };

    check();
    const interval = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        padding: "10px 16px",
        background: "#1f2937",
        color: "#fff",
        fontSize: "14px",
        fontWeight: 500,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <span>Hay una nueva versión disponible.</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: "#22c55e",
          color: "#08311a",
          border: "none",
          borderRadius: "6px",
          padding: "6px 14px",
          fontWeight: 700,
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        Actualizar
      </button>
    </div>
  );
}

export default VersionChecker;
